// frontend/src/pages/Certificates.tsx

import { useEffect, useState } from 'react';
import { useAuthStore } from '../store/authStore';
import { supabase } from '../lib/supabaseClient';
import { authService } from '../services/auth.service';
import '../styles/pricing.css';

const API_BASE = (import.meta.env.PROD && (import.meta.env.VITE_API_BASE_URL === 'http://localhost:8080' || !import.meta.env.VITE_API_BASE_URL)) ? '' : (import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080');

interface CertRow {
  id: string;
  is_verified: boolean;
  verification_code: string;
  issued_at: string;
  courses: { title: string } | null;
}

export function CertificatesPage() {
  const userId = useAuthStore((s) => s.session?.user.id);
  const [certs, setCerts] = useState<CertRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [upgradingId, setUpgradingId] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) return;
    Promise.resolve(
      supabase
        .from('certificates')
        .select('id, is_verified, verification_code, issued_at, courses(title)')
        .eq('student_id', userId)
        .order('issued_at', { ascending: false })
    )
      .then(({ data }) => setCerts((data as unknown as CertRow[]) ?? []))
      .finally(() => setIsLoading(false));
  }, [userId]);

  async function handleUpgrade(certId: string) {
    // TODO: ÖDEME SAĞLAYICI — 99 TL tahsilatı burada yapılacak
    setUpgradingId(certId);
    try {
      const token = await authService.getAccessToken();
      // Simülasyon: doğrudan is_verified = true yapıyoruz
      await supabase.from('certificates').update({ is_verified: true }).eq('id', certId);
      setCerts((prev) => prev.map((c) => (c.id === certId ? { ...c, is_verified: true } : c)));
      setMessage('✅ Sertifika doğrulandı! (Simülasyon — gerçek ödeme entegre edilecek)');
    } finally {
      setUpgradingId(null);
    }
  }

  return (
    <div className="pricing-page">
      <header className="pricing-header">
        <h1>Sertifikalarım 🎓</h1>
        <p>Tamamladığın kursların sertifikalarını gör ve paylaş</p>
      </header>

      {message && (
        <div className="quiz-error-banner" style={{ background: 'rgba(16,185,129,0.1)', borderColor: 'rgba(16,185,129,0.3)', color: 'var(--secondary)', marginBottom: 16 }}>
          {message}
        </div>
      )}

      {isLoading && <div className="courses-loading">Yükleniyor...</div>}

      {!isLoading && certs.length === 0 && (
        <div className="card admin-analytics-placeholder">
          <p>Henüz sertifikan yok. Bir kursu tamamladığında sertifikan otomatik oluşur.</p>
        </div>
      )}

      <div className="bootcamp-grid">
        {certs.map((cert) => (
          <div key={cert.id} className="bootcamp-card">
            <div className="bootcamp-card-header">
              <h3>{cert.courses?.title ?? 'Kurs'}</h3>
              {cert.is_verified ? (
                <span className="lesson-badge success">✅ Doğrulanmış</span>
              ) : (
                <span className="lesson-badge" style={{ background: 'rgba(99,102,241,0.1)', color: 'var(--primary-light)' }}>📄 Standart</span>
              )}
            </div>

            <p className="bootcamp-desc">
              Doğrulama Kodu: <code style={{ background: 'var(--dark)', padding: '2px 8px', borderRadius: 4, fontFamily: 'monospace' }}>{cert.verification_code}</code>
            </p>

            <div className="bootcamp-meta">
              <span>📅 {new Date(cert.issued_at).toLocaleDateString('tr-TR')}</span>
            </div>

            {!cert.is_verified && (
              <button
                type="button"
                className="lesson-btn secondary full"
                onClick={() => handleUpgrade(cert.id)}
                disabled={upgradingId === cert.id}
              >
                {upgradingId === cert.id ? '⏳...' : '🔒 Doğrulanmış Sertifikaya Yükselt — 99 ₺'}
              </button>
            )}

            {cert.is_verified && (
              <div className="pricing-current-badge" style={{ fontSize: 12 }}>
                LinkedIn'e ekleyebileceğin doğrulanmış sertifika! 🎉
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="card pricing-payment-note" style={{ marginTop: 24 }}>
        <h3>🎓 Sertifika Hakkında</h3>
        <p>
          <strong>Standart Sertifika</strong> ücretsizdir, tüm kurs tamamlayanlar alır.
          <strong> Doğrulanmış Sertifika (99 ₺)</strong> MinikCoder tarafından onaylanmış, 
          LinkedIn'de paylaşılabilir bir dijital sertifikadır. Ödeme sistemi simülasyon modundadır.
        </p>
      </div>
    </div>
  );
}
